import { useState } from 'react';
import { CheckCircle2, XCircle, Trophy, RotateCcw, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import type { QuizQuestion } from '@/lib/types';

interface QuizProps {
  questions: QuizQuestion[];
}

export function Quiz({ questions }: QuizProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [isFinished, setIsFinished] = useState(false);

  const question = questions[currentQuestion];
  const isCorrect = selectedAnswer === question.correctIndex;
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleSelectAnswer = (index: number) => {
    if (showResult) return;
    setSelectedAnswer(index);
    setShowResult(true);
    if (index === question.correctIndex) {
      setCorrectAnswers((prev) => prev + 1);
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setIsFinished(true);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setCorrectAnswers(0);
    setIsFinished(false);
  };

  const getScoreMessage = () => {
    const percentage = (correctAnswers / questions.length) * 100;
    if (percentage === 100) return 'Perfekt! Du hast alles richtig beantwortet!';
    if (percentage >= 80) return 'Sehr gut! Du hast das Thema gut verstanden.';
    if (percentage >= 60) return 'Gut gemacht! Ein paar Konzepte könntest du noch vertiefen.';
    if (percentage >= 40) return 'Nicht schlecht, aber lies den Artikel nochmal durch.';
    return 'Schau dir den Artikel nochmal genauer an und versuche es erneut.';
  };

  if (isFinished) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-card to-primary/5">
        <CardContent className="py-12 text-center">
          <div className="max-w-md mx-auto space-y-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto animate-fade-in">
              <Trophy className="w-10 h-10 text-primary" />
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '100ms' }}>
              <h3 className="text-2xl font-bold mb-2">Quiz abgeschlossen!</h3>
              <p className="text-4xl font-bold text-primary mb-2">
                {correctAnswers} / {questions.length}
              </p>
              <p className="text-muted-foreground">{getScoreMessage()}</p>
            </div>
            <Button
              onClick={handleRestart}
              variant="outline"
              className="gap-2 animate-fade-in"
              style={{ animationDelay: '200ms' }}
            >
              <RotateCcw className="w-4 h-4" />
              Quiz wiederholen
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-card to-primary/5">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between mb-4">
          <CardTitle className="text-lg">Wissenstest</CardTitle>
          <span className="text-sm text-muted-foreground">
            Frage {currentQuestion + 1} von {questions.length}
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </CardHeader>
      <CardContent className="space-y-6">
        <h3 className="text-xl font-semibold">{question.question}</h3>

        <div className="space-y-3">
          {question.options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrectAnswer = index === question.correctIndex;

            return (
              <button
                key={index}
                onClick={() => handleSelectAnswer(index)}
                disabled={showResult}
                className={cn(
                  'w-full text-left p-4 rounded-lg border-2 transition-all duration-200',
                  showResult
                    ? isCorrectAnswer
                      ? 'border-green-500 bg-green-500/10'
                      : isSelected
                      ? 'border-red-500 bg-red-500/10'
                      : 'border-border bg-card opacity-50'
                    : isSelected
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-card hover:border-primary/50 hover:bg-primary/5'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className={cn(showResult && isCorrectAnswer && 'font-medium')}>
                    {option}
                  </span>
                  {showResult && isCorrectAnswer && (
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  )}
                  {showResult && isSelected && !isCorrectAnswer && (
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {showResult && (
          <div
            className={cn(
              'p-4 rounded-lg animate-fade-in',
              isCorrect ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'
            )}
          >
            <p className={cn('font-medium', isCorrect ? 'text-green-500' : 'text-red-500')}>
              {isCorrect ? 'Richtig!' : 'Leider falsch.'}
            </p>
          </div>
        )}

        {showResult && (
          <Button onClick={handleNext} className="w-full gap-2 animate-fade-in">
            {currentQuestion < questions.length - 1 ? (
              <>
                Nächste Frage
                <ChevronRight className="w-4 h-4" />
              </>
            ) : (
              <>
                Ergebnis ansehen
                <Trophy className="w-4 h-4" />
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
